/**
 * What the vaktplan grid derives from the API rows before it renders: per
 * person and day, the shifts, the absence, the availability; per person the
 * week; per shift its daily entries. Computed once per data change and
 * memoised by the page, so a row re-renders from lookups only (plan 0019,
 * Performance).
 */
import type { ApiAbsence, ApiAvailability, ApiOffer, ApiShift, ApiSwap, ApiUser } from '@/api/data';
import { T, translate, useI18n } from '@/i18n';
import {
  type Entry,
  PERSON_CODES,
  dayEntries,
  hours,
  oslo,
  tenantRules,
  toPerson,
  weekOf,
  worst,
} from '@/rules';
import type { Tenant } from '@/shell/tenant';
import { num } from '@/ui/format';
import { type Person, addDays, dow, inRange, mondayOf } from '@timefly/core';
import { type Option, ranked } from './advice';

export const dateOf = (s: ApiShift): string => oslo(s.start_time)?.date ?? '';
export const timeText = (s: ApiShift): string => {
  const a = s.punch_in_time ? oslo(s.punch_in_time)?.time : oslo(s.start_time)?.time;
  const b = s.punch_in_time ? (oslo(s.punch_out_time)?.time ?? '?') : oslo(s.end_time)?.time;
  return `${a}–${b}`;
};
/**
 * Row 1 of shift_types is global: no tenant, returned only to the companies with shifts on it (admin
 * shiftTypeController getAllShiftTypes). It was named 'Unhandled' in the database and shown here as
 * «Standard»; since 2026-10-22 (#190) it is stored as «Standard», and the old name still reads as that.
 */
export const typeLabel = (t: { id: number; name: string } | null | undefined): string | undefined =>
  t ? (t.id === 1 && t.name === 'Unhandled' ? T('Standard') : t.name) : undefined;
export const friendlyType = <X extends { id: number; name: string }>(t: X): X => ({
  ...t,
  name: typeLabel(t) as string,
});
/** The day a new shift is proposed on: today when the window holds it, else the window's first day. */
export const focusDay = (days: string[], today: string): string =>
  days.includes(today) ? today : (days[0] ?? today);
/** 0 no punch, 1 in only, 2 in and out. */
export const punchState = (s: ApiShift): 0 | 1 | 2 => (s.punch_in_time ? (s.punch_out_time ? 2 : 1) : 0);

/**
 * What the four marks on a card are for: colour means somebody has to do something (owner,
 * 2026-09-21). Nothing else earns it. "Godkjent" was green and needs nobody, which is how the
 * card ended up teaching two contradictory rules at once.
 *
 * `quiet` draws no box at all, so a card with nothing outstanding is title, time and space. The
 * slots keep their positions, so a column can still be scanned down twenty people.
 *
 * Most of this turns on whether the shift has ENDED, which the card never knew. An unpunched
 * shift next Tuesday is normal; an unpunched shift last Tuesday is a hole in the pay run.
 */
export type Tone = 'quiet' | 'done' | 'act' | 'bad';
export interface Marks {
  punch: Tone;
  approval: Tone;
  request: Tone;
}

export function cardMarks(s: ApiShift, now: number = Date.now()): Marks {
  const end = Date.parse(s.end_time ?? '');
  const ended = Number.isFinite(end) && end < now;
  const p = punchState(s);

  // bad, not act: nobody clocked anything and the shift is over, so the hours do not exist
  const punch: Tone = p === 2 ? 'quiet' : p === 1 ? 'act' : ended ? 'bad' : 'quiet';

  // The one deliberate exception to "colour means act" (owner, 2026-09-21): approval is the end
  // of a shift's life and has money attached, so it is worth showing as proof rather than as the
  // absence of a problem. Publishing does NOT get the same: once a week is out every card would
  // be green, and colour everywhere is colour nowhere.
  //
  // An unworked past shift is already red on the punch mark; flagging it twice says nothing new,
  // so approval only asks once there is something to approve.
  const approval: Tone = s.is_approved ? 'done' : ended && p > 0 ? 'act' : 'quiet';

  const request: Tone = s.is_swap_requested || s.is_offer_requested ? 'act' : 'quiet';

  // Publishing is deliberately NOT a mark. The diagonal hatch across the card already says which
  // shifts are drafts, and the toolbar already reads "Publiser N utkast", a count and the action
  // in one button. A fourth mark would be the third place saying the same thing (owner,
  // 2026-09-21).
  return { punch, approval, request };
}

/**
 * What an absence card shows, under the same rule as a shift: colour means somebody must act.
 *
 * Sickness is its own case (owner, 2026-09-21). An ordinary absence request is a plan waiting
 * for a decision and can sit; sick leave has already happened, the person is not at work, and an
 * undecided one is a hole in the rota and in the pay run. So sick is green when approved and red
 * whenever it is not, and it never wears a checkmark: a tick reads as "approved" and the red
 * case is the opposite of that.
 *
 * `is_sick_day` on the type is the signal. On the production copy four of the six sickness types
 * are not flagged, carrying 21 of 32 requests, so this is only as good as that column (counted
 * 2026-09-21). Matching the name instead would break the first time somebody types Sykmelding.
 *
 * Returning null means "draw the waiting text instead", which is what an ordinary pending
 * request has always done.
 */
export interface AbsenceMark {
  tone: Tone;
  glyph: string;
  label: string;
}

export function absenceMark(a: ApiAbsence): AbsenceMark | null {
  const sick = a.absence_type?.is_sick_day === true;
  const accepted = a.status === 'accepted';
  const declined = a.status === 'declined';

  if (sick) {
    return accepted
      ? { tone: 'done', glyph: '+', label: 'Sykefravær, godkjent' }
      : { tone: 'bad', glyph: '+', label: declined ? 'Sykefravær, avslått' : 'Sykefravær, ikke godkjent' };
  }
  if (accepted) return { tone: 'done', glyph: '✓', label: 'Godkjent' };
  // Anything not pending used to fall through to a green tick reading "Godkjent", so a DECLINED
  // absence showed as approved. Found while building the sickness rule, 2026-09-21.
  if (declined) return { tone: 'bad', glyph: '✕', label: 'Avslått' };
  return null;
}

/** True when nothing on the card is asking for anything. `done` is not asking. */
export const allQuiet = (m: Marks): boolean => Object.values(m).every((t) => t === 'quiet' || t === 'done');

export type Avail = { kind: 'unavail' } | { kind: 'partly'; from: string; to: string };

/**
 * What the plan reads from a row (issue #70, as the server's planned() in services/poc/reads.js): while
 * a change waits or after it was rejected, the values approved before; a rejected row never approved is
 * nothing.
 */
function plannedRow(r: ApiAvailability): ApiAvailability | null {
  if (r.approval_status !== 'pending' && r.approval_status !== 'rejected') return r;
  if (r.approved_start_date_time)
    return {
      ...r,
      start_date_time: r.approved_start_date_time,
      end_date_time: r.approved_end_date_time ?? r.end_date_time,
      is_available: r.approved_is_available ?? r.is_available,
      is_preference: r.approved_is_preference ?? false,
    };
  return r.approval_status === 'rejected' ? null : r;
}

/**
 * The availability that applies to a person on a day, from the rows the admin route returns. A wish to
 * work (is_preference) limits nothing, as on the server (services/poc/reads.js availabilityOn).
 */
export function availOn(rows: ApiAvailability[], userId: number, day: string): Avail | null {
  for (const row of rows) {
    if (row.user_id !== userId) continue;
    const r = plannedRow(row);
    if (!r || (r.is_available && r.is_preference)) continue;
    const s = oslo(r.start_date_time);
    const e = oslo(r.end_date_time);
    if (!s || !e) continue;
    let hit = false;
    if (r.is_recurring) {
      const until = r.recurrence_end_date ? (oslo(r.recurrence_end_date)?.date ?? day) : day;
      // weekly the same weekday, monthly the same day of the month, as the server reads it (#254, PL-03)
      if (day >= s.date && day <= until)
        hit =
          r.recurrence_pattern === 'weekly'
            ? dow(day) === dow(s.date)
            : r.recurrence_pattern === 'monthly'
              ? day.slice(8, 10) === s.date.slice(8, 10)
              : true;
    } else hit = inRange(day, s.date, e.date);
    if (!hit) continue;
    if (!r.is_available) return { kind: 'unavail' };
    const whole = s.time <= '00:00' && e.time >= '23:59';
    if (!whole) return { kind: 'partly', from: s.time, to: e.time };
  }
  return null;
}

export interface Cell {
  shifts: ApiShift[];
  absence: ApiAbsence | null;
  avail: Avail | null;
  sev: 'd' | 'w' | '';
}
export interface Row {
  user: ApiUser;
  person: Person;
  cells: Map<string, Cell>;
  week: ReturnType<typeof weekOf>;
  sev: 'd' | 'w' | '';
  /** Everything this person breaks in the window, the day rules and the week's, in one list. */
  alerts: Entry[];
}
/** One person's rule breaches, for a day column's popover. `user` is null for an unassigned shift. */
export interface AlertGroup {
  user: ApiUser | null;
  entries: Entry[];
}
export interface Grid {
  rows: Row[];
  open: Map<string, ApiShift[]>;
  entries: Map<number, Entry[]>;
  colSev: Map<string, 'd' | 'w' | ''>;
  /** Per day, who breaks what. The header shows the count and lists them on demand. */
  colAlerts: Map<string, AlertGroup[]>;
  drafts: number;
  /**
   * Drafts in the window that break a rule, so publish can say so (ruling 2026-09-08: breaches
   * are shown at publish and at approve, never a block). Shift-level entries only.
   */
  draftBreaches: { shift: ApiShift; entries: Entry[] }[];
  assigned: number;
}

export function buildGrid(
  days: string[],
  shifts: ApiShift[],
  users: ApiUser[],
  absences: ApiAbsence[],
  avail: ApiAvailability[],
  tenant: Tenant | undefined,
): Grid {
  const rules = tenantRules();
  const first = days[0] ?? '';
  const entries = new Map<number, Entry[]>();
  const byUser = new Map<number, ApiShift[]>();
  const open = new Map<string, ApiShift[]>();
  for (const s of shifts) {
    const d = dateOf(s);
    if (s.user_id == null) {
      if (days.includes(d)) open.set(d, [...(open.get(d) ?? []), s]);
      continue;
    }
    byUser.set(s.user_id, [...(byUser.get(s.user_id) ?? []), s]);
  }
  const rows: Row[] = users
    .filter((u) => u.active_status)
    .map((u) => {
      const person = toPerson(u, tenant);
      const mine = byUser.get(u.id) ?? [];
      const cells = new Map<string, Cell>();
      let rowSev: 'd' | 'w' | '' = '';
      for (const d of days) {
        const list = mine.filter((s) => dateOf(s) === d);
        const es = list.flatMap((s) => {
          const e = dayEntries(s, person, rules);
          entries.set(s.id, e);
          return e;
        });
        const sev = worst(es);
        if (sev === 'd' || (sev === 'w' && !rowSev)) rowSev = sev;
        const absence =
          absences.find(
            (a) =>
              a.user_id === u.id &&
              a.status !== 'declined' &&
              inRange(
                d,
                oslo(a.start_date)?.date ?? '',
                oslo(a.end_date)?.date ?? oslo(a.start_date)?.date ?? '',
              ),
          ) ?? null;
        cells.set(d, { shifts: list, absence, avail: availOn(avail, u.id, d), sev });
      }
      // Uke is one week. To uker and Måned add up the days on screen against the agreed week
      // pro rata, and carry every week's rule entries; the first week alone read "0 av 37,5"
      // for a month that had started on a Sunday.
      const weeks = [...new Set(days.map(mondayOf))].map((m) => weekOf(person, m, mine, rules));
      const w0 = weeks[0] ?? weekOf(person, mondayOf(first), mine, rules);
      const week =
        weeks.length < 2
          ? w0
          : {
              hours: mine.filter((s) => days.includes(dateOf(s))).reduce((a, s) => a + hours(s, person), 0),
              norm: w0.norm == null ? null : Math.round((w0.norm * days.length * 10) / 7) / 10,
              entries: weeks.flatMap((w) => w.entries),
            };
      const wsev = worst(week.entries);
      // Only the days on screen: a shift in a week nobody is looking at is not this row's alert.
      // Each day entry carries its day, and an entry about the person (age unknown) is said once:
      // it repeated on every shift, burying the week rule (#35, production 2026-10-04).
      const said = new Set<string>();
      const dayAlerts = days.flatMap((d) =>
        (cells.get(d)?.shifts ?? []).flatMap((s) =>
          (entries.get(s.id) ?? []).flatMap((e) => {
            if (!PERSON_CODES.has(e.code)) return [{ ...e, day: d }];
            if (said.has(e.code)) return [];
            said.add(e.code);
            return [e];
          }),
        ),
      );
      return {
        user: u,
        person,
        cells,
        week,
        sev: wsev === 'd' ? 'd' : rowSev || wsev,
        // the week first: it is the one that differs, and the one that matters most
        alerts: [...week.entries, ...dayAlerts],
      };
    });
  for (const s of open.values())
    for (const x of s) entries.set(x.id, dayEntries(x, { id: 'open', norm: 37.5 }, rules));
  const colSev = new Map<string, 'd' | 'w' | ''>();
  const colAlerts = new Map<string, AlertGroup[]>();
  for (const d of days) {
    const all = [...rows.flatMap((r) => r.cells.get(d)?.shifts ?? []), ...(open.get(d) ?? [])];
    colSev.set(d, worst(all.flatMap((s) => entries.get(s.id) ?? [])));
    // Grouped by person rather than flattened: "Ola 2, Kari 1" is actionable and a list of
    // five rule names is not. Unassigned shifts break rules too and come last, under no name.
    const groups: AlertGroup[] = [];
    for (const r of rows) {
      const all = (r.cells.get(d)?.shifts ?? []).flatMap((s) => entries.get(s.id) ?? []);
      // two shifts on one day repeat what is about the person; once is enough (#35)
      const es = all.filter(
        (e, i) => !PERSON_CODES.has(e.code) || all.findIndex((x) => x.code === e.code) === i,
      );
      if (es.length) groups.push({ user: r.user, entries: es });
    }
    const openEs = (open.get(d) ?? []).flatMap((s) => entries.get(s.id) ?? []);
    if (openEs.length) groups.push({ user: null, entries: openEs });
    colAlerts.set(d, groups);
  }
  const inWindow = shifts.filter((s) => days.includes(dateOf(s)));
  return {
    rows,
    open,
    entries,
    colSev,
    colAlerts,
    drafts: inWindow.filter((s) => !s.published).length,
    draftBreaches: inWindow
      .filter((s) => !s.published)
      .map((s) => ({ shift: s, entries: (entries.get(s.id) ?? []).filter((e) => e.sev === 'd') }))
      .filter((x) => x.entries.length > 0)
      .sort((a, b) => a.shift.start_time.localeCompare(b.shift.start_time)),
    assigned: inWindow.filter((s) => s.user_id != null).length,
  };
}

export const hoursOfShift = (s: ApiShift, p: Person | null) => hours(s, p);

/**
 * The line under a day's date, "12 vakter · 86,5 t": the shifts the grid shows that day, open
 * ones included, and their hours by the same rule as the Sum row (plan 0025, Vaktplan).
 */
export const daySummary = (list: ApiShift[]): string =>
  `${list.length} ${list.length === 1 ? 'vakt' : 'vakter'} · ${num(list.reduce((a, s) => a + hours(s, null), 0))} t`;
export const nextDay = addDays;

/**
 * How the Ansatte head sorts the rows, and the choice the manager keeps.
 *
 * The old timeline offered two orders, "Alfabetisk" and "Startdato", and remembered the
 * choice in localStorage under `sortBy`
 * (`frontend/src/app/(DashboardLayout)/admin/timeline/page.tsx:96`, and
 * `.../timeline/resource/ResourseHeaderContent.tsx`). Startdato was FullCalendar's
 * `earliestEventStart`: the resource whose first event in the window starts earliest
 * comes first. `earliestStart` is that rule, and a person with nothing in the window
 * sorts last, as a resource with no events did.
 */
export const SORT: [string, string][] = [
  ['navn', 'Navn'],
  ['start', 'Startdato'],
  ['timer', 'Flest timer'],
  ['avvik', 'Størst avvik fra avtalt'],
];
export const SORT_KEY = 'vaktplan.sort';
/** The stored order, or 'navn' when nothing is stored, the value is unknown, or storage throws. */
export const readSort = (): string => {
  try {
    const v = localStorage.getItem(SORT_KEY);
    return SORT.some(([k]) => k === v) ? (v as string) : 'navn';
  } catch {
    return 'navn';
  }
};
/** Storage is a privacy setting the person can switch off; a refusal must not take the page down. */
export const writeSort = (key: string): void => {
  try {
    localStorage.setItem(SORT_KEY, key);
  } catch {
    /* the sort still applies for this session */
  }
};
export const earliestStart = (row: Pick<Row, 'cells'>): number => {
  let first = Number.POSITIVE_INFINITY;
  for (const cell of row.cells.values())
    for (const s of cell.shifts) {
      const t = new Date(s.start_time).getTime();
      if (!Number.isNaN(t) && t < first) first = t;
    }
  return first;
};

/** How many days the local month containing `d` has. Leap years included, no library. */
export const monthLength = (d: string): number =>
  new Date(Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)), 0)).getUTCDate();

/** The first day of the month containing `d`, and of the month `n` months away. */
export const monthStart = (d: string, n = 0): string => {
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7)) - 1 + n;
  return new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
};

/** What the panel beside the week shows for one shift, derived from what the page already holds. */
export interface PanelContext {
  s: ApiShift;
  /** the rule entries the server sent for this shift */
  entries: Entry[];
  /** the person's absence that day, any status; null for an open shift */
  absence: ApiAbsence | null;
  /** a pending offer or swap waiting on a manager for this shift */
  offer: ApiOffer | null;
  swap: ApiSwap | null;
  /** who could take it, best first with the reason (advice.ts ranked, issue #131): only for an open
      shift, or one whose person is away (not declined) that day */
  cover: (Option | Omit<Option, 'before' | 'after' | 'adds' | 'seen' | 'safe' | 'reason'>)[] | null;
  /** timesheet.view: the cover may say hours (issue #131); absent reads as yes */
  showHours?: boolean;
  /** the other shifts on the same day, for moving a task or the edge between two shifts */
  sameDay: ApiShift[];
}

/** The panel's context for a shift (issue #75): pure, so the panel mounts alone from one object. */
export function panelContextOf(
  s: ApiShift,
  grid: Pick<Grid, 'rows' | 'entries'>,
  offers: ApiOffer[],
  swaps: ApiSwap[],
  shifts: ApiShift[],
  /** the catalogue's translate, for a rule the taker would touch, and whether hours may be said */
  opts: {
    t?: (k: string, v?: Record<string, string | number>) => string;
    showHours?: boolean;
    /** the reader, left out of Hugin's ranking (advice.ts ranked) */
    me?: number | null;
  } = {},
): PanelContext {
  const d = dateOf(s);
  const absence =
    s.user_id != null
      ? (grid.rows.find((r) => r.user.id === s.user_id)?.cells.get(d)?.absence ?? null)
      : null;
  return {
    s,
    entries: grid.entries.get(s.id) ?? [],
    absence,
    offer: offers.find((o) => o.shift_id === s.id) ?? null,
    swap: swaps.find((x) => x.shift_id_one === s.id || x.shift_id_two === s.id) ?? null,
    cover:
      s.user_id == null || (absence && absence.status !== 'declined')
        ? ranked(
            s,
            grid.rows as Row[],
            shifts,
            opts.t ?? ((k, v) => translate(useI18n.getState().locale, k, v)),
            opts.showHours ?? true,
            opts.me ?? null,
          )
        : null,
    showHours: opts.showHours ?? true,
    sameDay: shifts.filter((x) => x.id !== s.id && dateOf(x) === d),
  };
}
